import {
  type GachaEvmBuybackInput,
  type GachaEvmBuybackQuote,
  gachaEvmBuybackAbi,
  gachaEvmBuybackAvailableSchema,
  gachaEvmBuybackCheckSchema,
  gachaEvmBuybackQuoteSchema,
  validateGachaEvmBuybackQuote,
} from "@anaconda/core/gacha/evm-buyback"
import {
  type GachaEnvironment,
  gachaEvmAddressSchema,
  gachaEvmReadinessSchema,
  getGachaEvmChains,
} from "@anaconda/core/gacha/evm-chains"
import { wrap } from "@reatom/core"
import {
  http,
  createPublicClient,
  decodeEventLog,
  defineChain,
  encodeFunctionData,
  erc20Abi,
  formatUnits,
  numberToHex,
} from "viem"
import { z } from "zod"
import { type GachaApiOptions, getGachaApiUrl } from "./gacha-api"
import { isGachaEvmBuybackTransaction } from "./gacha-evm-buyback-transaction"
import type { GachaEvmTransferWallet } from "./portfolio/gacha-evm-transfer"

const hashSchema = z
  .string()
  .regex(/^0x[0-9a-fA-F]{64}$/)
  .transform((value): `0x${string}` => `0x${value.slice(2)}`)
export function formatGachaEvmBuyback(
  quote: GachaEvmBuybackQuote,
  notation: "token" | "usd" = "token",
) {
  const amount = formatUnits(
    BigInt(quote.refundAmountBase),
    quote.token.decimals,
  )
  return notation === "usd" ? `$${amount}` : `${amount} ${quote.token.symbol}`
}

export function formatGachaEvmBuybackButtonPrice(
  quote: GachaEvmBuybackQuote,
  notation: "token" | "usd" = "token",
) {
  const scale = BigInt(10) ** BigInt(quote.token.decimals)
  const cents =
    (BigInt(quote.refundAmountBase) * BigInt(100) + scale / BigInt(2)) / scale
  const whole = (cents / BigInt(100)).toLocaleString("en-US")
  const fraction = (cents % BigInt(100)).toString().padStart(2, "0")
  const amount = `${whole}.${fraction}`
  return notation === "usd" ? `$${amount}` : `${amount} ${quote.token.symbol}`
}

export type GachaEvmBuybackOffer = {
  quote: GachaEvmBuybackQuote
  eligibilityUntil: number
}

export type GachaEvmBuybackWallet = GachaEvmTransferWallet & {
  authorizeConnection?: () => Promise<void>
}

// One session pins the original wallet/card/lane, keeps live quotes, and retains
// an ambiguous broadcast hash so Retry reconciles rather than signs a second sale.
export function createGachaEvmBuyback(
  input: GachaEvmBuybackInput,
  runtime: {
    environment: GachaEnvironment
    getWallet: () => Promise<GachaEvmBuybackWallet>
    assertContext: () => void
    apiOptions: GachaApiOptions
    refresh: () => void
  },
) {
  const chain = getGachaEvmChains(runtime.environment).find(
    (chain) => chain.chainId === input.chainId,
  )
  if (
    !chain ||
    chain.cardContract.toLowerCase() !== input.evmContract.toLowerCase()
  )
    throw new Error("Original buyback wallet or card unavailable")
  const rpc = createPublicClient({
    chain: defineChain({
      id: chain.chainId,
      name: chain.name,
      nativeCurrency: chain.nativeCurrency,
      rpcUrls: { default: { http: [chain.rpcUrl] } },
    }),
    transport: http(chain.rpcUrl, { retryCount: 0, timeout: 15000 }),
  })
  let quote: GachaEvmBuybackQuote | null = null
  let transaction: { hash: `0x${string}`; quote: GachaEvmBuybackQuote } | null =
    null
  let uncertainQuote: GachaEvmBuybackQuote | null = null
  let complete = false
  let eligibilityUntil = 0
  const json = async (path: string, body?: object) => {
    const response = await wrap(
      fetch(getGachaApiUrl(path, runtime.apiOptions), {
        cache: "no-store",
        signal: AbortSignal.timeout(15000),
        ...(body
          ? {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }
          : {}),
      }),
    )
    const data = await wrap(response.json())
    if (!response.ok || response.status === 202) {
      const error = z
        .object({
          message: z.string().optional(),
          error: z.string().optional(),
          code: z.string().optional(),
          retryAfterMs: z.number().nonnegative().optional(),
        })
        .safeParse(data)
      const message = error.success
        ? (error.data.message ??
          error.data.error ??
          error.data.code ??
          "Buyback API unavailable; retry later.")
        : "Buyback API unavailable; retry later."
      throw new Error(
        error.success && error.data.retryAfterMs !== undefined
          ? `${message}. Retry in ${Math.ceil(error.data.retryAfterMs / 1000)}s.`
          : message,
      )
    }
    return data
  }
  const checkOwner = async () => {
    runtime.assertContext()
    if ((await wrap(rpc.getChainId())) !== input.chainId)
      throw new Error("Buyback RPC network mismatch")
    const owner = await wrap(
      rpc.readContract({
        address: input.evmContract,
        abi: gachaEvmBuybackAbi,
        functionName: "ownerOf",
        args: [BigInt(input.evmTokenId)],
      }),
    )
    if (owner.toLowerCase() !== input.playerAddress.toLowerCase())
      throw new Error("This card is no longer owned by the original wallet.")
  }
  const readiness = async () => {
    const state = gachaEvmReadinessSchema.parse(await wrap(json("/evm/chains")))
    const ready = state.chains.find((chain) => chain.chainId === input.chainId)
    const operation = ready?.operations.buyback
    if (
      state.environment !== runtime.environment ||
      !operation ||
      (!operation.available && operation.reason !== "paused") ||
      ready.cardContract.toLowerCase() !== input.evmContract.toLowerCase() ||
      ready.vaultContract.toLowerCase() !== chain.vaultContract.toLowerCase()
    )
      throw new Error("Buyback configuration unavailable or mismatched")
  }
  const review = (offer: GachaEvmBuybackOffer): GachaEvmBuybackOffer => {
    if (transaction) return { quote: transaction.quote, eligibilityUntil }
    if (uncertainQuote) return { quote: uncertainQuote, eligibilityUntil }
    runtime.assertContext()
    if (
      offer.quote.deadline * 1000 <= Date.now() ||
      offer.eligibilityUntil <= Date.now()
    )
      throw new Error("Buyback quote expired; renew and confirm the new quote.")
    validateGachaEvmBuybackQuote(
      runtime.environment,
      {
        ...input,
        paymentToken: input.paymentToken ?? quote?.paymentToken,
      },
      offer.quote,
    )
    quote = offer.quote
    eligibilityUntil = offer.eligibilityUntil
    return { quote, eligibilityUntil }
  }
  const prepare = async (
    offer?: GachaEvmBuybackOffer,
  ): Promise<GachaEvmBuybackOffer> => {
    if (transaction) return { quote: transaction.quote, eligibilityUntil }
    if (uncertainQuote) return { quote: uncertainQuote, eligibilityUntil }
    await wrap(checkOwner())
    await wrap(readiness())
    const pinnedInput = {
      ...input,
      paymentToken: input.paymentToken ?? quote?.paymentToken,
    }
    if (
      offer &&
      offer.quote.deadline * 1000 > Date.now() &&
      offer.eligibilityUntil > Date.now()
    )
      return review(offer)
    if (
      quote &&
      quote.deadline * 1000 > Date.now() &&
      eligibilityUntil > Date.now()
    ) {
      validateGachaEvmBuybackQuote(runtime.environment, input, quote)
      runtime.assertContext()
      return { quote, eligibilityUntil }
    }
    const available = gachaEvmBuybackAvailableSchema.parse(
      await wrap(
        json(
          `/evm/buyback/available?${new URLSearchParams({ wallet: input.playerAddress, tokenId: input.evmTokenId, contract: input.evmContract, chainId: String(input.chainId) })}`,
        ),
      ),
    )
    if (
      !available.available ||
      available.chainId !== input.chainId ||
      available.timeRemaining === undefined ||
      available.timeRemaining <= 0
    )
      throw new Error("This card is not eligible for buyback.")
    eligibilityUntil = Date.now() + available.timeRemaining * 1000
    if (!quote || quote.deadline * 1000 <= Date.now()) {
      // Portfolio cards do not carry purchase history. Their first verified API
      // quote establishes the original lane, which renewal must not replace.
      const candidate = gachaEvmBuybackQuoteSchema.parse(
        await wrap(json("/evm/buyback", pinnedInput)),
      )
      validateGachaEvmBuybackQuote(runtime.environment, pinnedInput, candidate)
      if (candidate.deadline * 1000 <= Date.now())
        throw new Error("Buyback quote expired; request a new quote.")
      quote = candidate
    }
    runtime.assertContext()
    return { quote, eligibilityUntil }
  }
  const reconcile = async () => {
    if (!transaction) throw new Error("Buyback transaction unavailable")
    const { hash, quote: sold } = transaction
    if ((await wrap(rpc.getChainId())) !== input.chainId)
      throw new Error("Buyback RPC network mismatch; no payout confirmed.")
    const receipt = await wrap(
      rpc.waitForTransactionReceipt({ hash, confirmations: 2, timeout: 60000 }),
    )
    if (receipt.status !== "success") {
      transaction = null
      throw new Error("Buyback transaction reverted; no payout confirmed.")
    }
    const tx = await wrap(rpc.getTransaction({ hash }))
    if (
      tx.hash.toLowerCase() !== hash.toLowerCase() ||
      receipt.transactionHash.toLowerCase() !== hash.toLowerCase() ||
      // Legacy RPC transaction responses may omit chainId; the RPC network
      // was verified above. An explicit conflicting chain is still rejected.
      (tx.chainId !== undefined && tx.chainId !== input.chainId) ||
      !isGachaEvmBuybackTransaction(tx, input, sold)
    )
      throw new Error(
        "Buyback transaction context does not match the quote; no payout confirmed.",
      )
    const paid = receipt.logs.some((log) => {
      if (log.address.toLowerCase() !== sold.paymentToken.toLowerCase())
        return false
      try {
        const event = decodeEventLog({
          abi: erc20Abi,
          data: log.data,
          topics: log.topics,
        })
        return (
          event.eventName === "Transfer" &&
          event.args.from.toLowerCase() === sold.vault.toLowerCase() &&
          event.args.to.toLowerCase() === input.playerAddress.toLowerCase() &&
          event.args.value === BigInt(sold.refundAmountBase)
        )
      } catch {
        return false
      }
    })
    const removed = receipt.logs.some((log) => {
      if (log.address.toLowerCase() !== input.evmContract.toLowerCase())
        return false
      try {
        const event = decodeEventLog({
          abi: gachaEvmBuybackAbi,
          data: log.data,
          topics: log.topics,
        })
        return (
          event.eventName === "Transfer" &&
          event.args.from.toLowerCase() === input.playerAddress.toLowerCase() &&
          event.args.to.toLowerCase() === sold.vault.toLowerCase() &&
          event.args.tokenId === BigInt(input.evmTokenId)
        )
      } catch {
        return false
      }
    })
    if (!paid || !removed)
      throw new Error(
        "Receipt did not confirm the exact payout and card sale. Retry reconciliation; do not sell again.",
      )
    // Settlement is optional bookkeeping. RPC payment evidence is mandatory.
    let settlementPending = false
    try {
      await wrap(
        json("/evm/buyback/settle", { txHash: hash, chainId: input.chainId }),
      )
    } catch {
      settlementPending = true
    }
    try {
      const check = gachaEvmBuybackCheckSchema.parse(
        await wrap(
          json(`/evm/buyback/check?memo=${encodeURIComponent(sold.memo)}`),
        ),
      )
      settlementPending =
        !check.exists ||
        check.status !== "complete" ||
        check.userWallet?.toLowerCase() !== input.playerAddress.toLowerCase() ||
        check.evmTokenId !== sold.tokenId ||
        check.evmContractAddress?.toLowerCase() !==
          sold.cardContract.toLowerCase() ||
        check.refundAmountBase !== sold.refundAmountBase ||
        check.quoteId !== sold.quoteId ||
        check.vault?.toLowerCase() !== sold.vault.toLowerCase() ||
        check.payoutTxHash?.toLowerCase() !== hash.toLowerCase()
    } catch {
      settlementPending = true
    }
    complete = true
    runtime.refresh()
    return { label: formatGachaEvmBuyback(sold), settlementPending, hash }
  }
  const sell = async () => {
    if (complete && transaction)
      return {
        label: formatGachaEvmBuyback(transaction.quote),
        settlementPending: false,
        hash: transaction.hash,
      }
    if (transaction) return await wrap(reconcile())
    if (uncertainQuote) {
      const check = gachaEvmBuybackCheckSchema.parse(
        await wrap(
          json(
            `/evm/buyback/check?memo=${encodeURIComponent(uncertainQuote.memo)}`,
          ),
        ),
      )
      if (
        !check.exists ||
        check.status !== "complete" ||
        check.quoteId !== uncertainQuote.quoteId ||
        !check.payoutTxHash
      )
        throw new Error(
          "Buyback signing outcome is unresolved. Check the original wallet and CollectorCrypt status; do not sell again.",
        )
      transaction = {
        hash: hashSchema.parse(check.payoutTxHash),
        quote: uncertainQuote,
      }
      uncertainQuote = null
      return await wrap(reconcile())
    }
    if (!quote || quote.deadline * 1000 <= Date.now())
      throw new Error("Buyback quote expired; renew and confirm the new quote.")
    const approvedQuote = quote
    validateGachaEvmBuybackQuote(runtime.environment, input, approvedQuote)
    await wrap(readiness())
    await wrap(checkOwner())
    const wallet = await wrap(runtime.getWallet())
    runtime.assertContext()
    if (wallet.address.toLowerCase() !== input.playerAddress.toLowerCase())
      throw new Error(
        "Connect the original buyback wallet to confirm this offer.",
      )
    if (wallet.authorizeConnection) await wrap(wallet.authorizeConnection())
    let provider = await wrap(wallet.getEthereumProvider())
    if (
      Number(await wrap(provider.request({ method: "eth_chainId" }))) !==
      input.chainId
    ) {
      await wrap(wallet.switchChain(input.chainId))
      provider = await wrap(wallet.getEthereumProvider())
    }
    const checkSigner = async () => {
      runtime.assertContext()
      if (
        Number(await wrap(provider.request({ method: "eth_chainId" }))) !==
        input.chainId
      )
        throw new Error("Wallet network changed; return to the card network.")
      const accounts = z
        .array(gachaEvmAddressSchema)
        .parse(await wrap(provider.request({ method: "eth_accounts" })))
      if (
        !accounts.some(
          (address) =>
            address.toLowerCase() === input.playerAddress.toLowerCase(),
        )
      )
        throw new Error("Original buyback signer is no longer connected.")
    }
    const send = async (
      to: `0x${string}`,
      data: `0x${string}`,
      gas: bigint,
      sale = false,
    ) => {
      await wrap(checkSigner())
      await wrap(checkOwner())
      if (approvedQuote.deadline * 1000 <= Date.now())
        throw new Error(
          "Buyback quote expired; renew and confirm the new quote.",
        )
      const price = await wrap(rpc.getGasPrice())
      if (
        (await wrap(rpc.getBalance({ address: input.playerAddress }))) <
        gas * price
      )
        throw new Error(
          `Insufficient ${chain.nativeCurrency.symbol} for gas on ${chain.name}.`,
        )
      await wrap(checkOwner())
      await wrap(checkSigner())
      if (approvedQuote.deadline * 1000 <= Date.now())
        throw new Error(
          "Buyback quote expired; renew and confirm the new quote.",
        )
      if (sale) uncertainQuote = approvedQuote
      try {
        return hashSchema.parse(
          await wrap(
            provider.request({
              method: "eth_sendTransaction",
              params: [
                {
                  from: input.playerAddress,
                  to,
                  data,
                  chainId: numberToHex(input.chainId),
                  gas: numberToHex(gas),
                  gasPrice: numberToHex(price),
                  value: "0x0",
                },
              ],
            }),
          ),
        )
      } catch (error) {
        // Only EIP-1193's explicit cancellation proves no broadcast. Generic
        // provider failures retain the quote for check/receipt reconciliation.
        if (
          sale &&
          z.object({ code: z.literal(4001) }).safeParse(error).success
        )
          uncertainQuote = null
        throw error
      }
    }
    await wrap(checkSigner())
    if (
      (await wrap(rpc.getBalance({ address: input.playerAddress }))) ===
      BigInt(0)
    )
      throw new Error(
        `Insufficient ${chain.nativeCurrency.symbol} for gas on ${chain.name}.`,
      )
    const approved = await wrap(
      rpc.readContract({
        address: input.evmContract,
        abi: gachaEvmBuybackAbi,
        functionName: "getApproved",
        args: [BigInt(input.evmTokenId)],
      }),
    )
    if (approved.toLowerCase() !== approvedQuote.vault.toLowerCase()) {
      const data = encodeFunctionData({
        abi: gachaEvmBuybackAbi,
        functionName: "approve",
        args: [approvedQuote.vault, BigInt(input.evmTokenId)],
      })
      const gas =
        ((await wrap(
          rpc.estimateGas({
            account: input.playerAddress,
            to: input.evmContract,
            data,
          }),
        )) *
          BigInt(120)) /
        BigInt(100)
      const price = await wrap(rpc.getGasPrice())
      if (
        (await wrap(rpc.getBalance({ address: input.playerAddress }))) <
        (gas + BigInt(approvedQuote.suggestedGasLimit)) * price
      )
        throw new Error(
          `Insufficient ${chain.nativeCurrency.symbol} for gas on ${chain.name}.`,
        )
      const approval = await wrap(send(input.evmContract, data, gas))
      if (
        (
          await wrap(
            rpc.waitForTransactionReceipt({
              hash: approval,
              confirmations: 2,
              timeout: 60000,
            }),
          )
        ).status !== "success"
      )
        throw new Error("NFT approval failed confirmation.")
    }
    await wrap(readiness())
    const data = encodeFunctionData({
      abi: gachaEvmBuybackAbi,
      functionName: "sellBack",
      args: [
        approvedQuote.paymentToken,
        BigInt(approvedQuote.tokenId),
        BigInt(approvedQuote.refundAmountBase),
        BigInt(approvedQuote.deadline),
        BigInt(approvedQuote.quoteId),
        approvedQuote.signature,
      ],
    })
    const hash = await wrap(
      send(
        approvedQuote.vault,
        data,
        BigInt(approvedQuote.suggestedGasLimit),
        true,
      ),
    )
    transaction = { hash, quote: approvedQuote }
    uncertainQuote = null
    return await wrap(reconcile())
  }
  return {
    review,
    prepare,
    sell,
    hasBroadcast: () => transaction !== null || uncertainQuote !== null,
  }
}
